import { useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { FieldArray, Form, Formik } from 'formik'
import * as Yup from 'yup'
import styled from 'styled-components'
import axios from 'lib/axios'
import { superAdminRoutes } from 'router/routes'
import { useNotification } from 'lib/context/notification.context'
import { Block } from 'views/components/UI/Contents'
import { Button, StrokedButton } from 'views/components/UI/Buttons'
import Input from 'views/components/UI/TextInput'
import Select from 'views/components/UI/Select'
import { H2, H3, P } from 'views/components/UI/Text'
import {
  ErrorContainer,
  InfoContainer,
  LastButtonsContainer,
  TextError,
} from './styled'

const Container = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
`

interface IRow {
  margin?: string
  width?: string
  justifyContent?: string
  alignItems?: string
  flexDirection?: string
  gap?: string
}

const Row = styled.div<IRow>`
  display: flex;
  flex-direction: ${(props) => props.flexDirection || 'row'};
  justify-content: ${(props) => props.justifyContent || 'space-between'};
  align-items: ${(props) => props.alignItems || 'flex-start'};
  width: ${(props) => props.width || '100%'};
  margin: ${(props) => props.margin || '0'};
  gap: ${(props) => props.gap || '0'};
`

const QuestionCard = styled.div`
  width: 100%;
  padding: 24px;
  margin-top: 20px;
  border-radius: 20px;
  background: #f4f8ff;
  box-shadow: 0px 2px 10px rgba(0, 0, 0, 0.08);
`

const OptionCard = styled.div`
  width: 100%;
  padding: 18px;
  margin-top: 14px;
  border-radius: 16px;
  background: #ffffff;
  border: 1px solid rgba(45, 116, 156, 0.12);
`

const ActionRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  margin-top: 20px;
  flex-wrap: wrap;
`

const TextButton = styled.p`
  cursor: pointer;
  color: #2d749c;
  font-weight: 700;
  margin: 0;
`

const STATUS_OPTIONS = [
  { value: 'draft', label: 'Borrador' },
  { value: 'published', label: 'Publicado' },
  { value: 'archived', label: 'Archivado' },
]

const QUESTION_TYPE_OPTIONS = [
  { value: 'single_choice', label: 'Selección única' },
  { value: 'multiple_choice', label: 'Selección múltiple' },
  { value: 'short_text', label: 'Abierta corta' },
  { value: 'long_text', label: 'Abierta larga' },
  { value: 'number', label: 'Numérica' },
  { value: 'date', label: 'Fecha' },
]

const CHOICE_TYPES = ['single_choice', 'multiple_choice']

type QuestionnaireOptionForm = {
  id?: number
  text: string
}

type QuestionnaireQuestionForm = {
  id?: number
  text: string
  type: string
  isRequired: boolean
  options: QuestionnaireOptionForm[]
}

type QuestionnaireFormValues = {
  name: string
  description: string
  status: string
  questions: QuestionnaireQuestionForm[]
}

type QuestionnairePayload = {
  name: string
  description: string
  status: string
  questions: Array<{
    id?: number
    text: string
    type: string
    is_required: boolean
    position: number
    options?: Array<{
      id?: number
      text: string
      position: number
    }>
  }>
}

interface IProps {
  initialValues?: QuestionnaireFormValues
  onSubmit?: (values: QuestionnairePayload) => Promise<void> | void
  submitLabel?: string
  successMessage?: string
  errorMessage?: string
}

const emptyOption = (): QuestionnaireOptionForm => ({
  text: '',
})

const emptyQuestion = (): QuestionnaireQuestionForm => ({
  text: '',
  type: 'single_choice',
  isRequired: true,
  options: [emptyOption()],
})

const getDefaultInitialValues = (): QuestionnaireFormValues => ({
  name: '',
  description: '',
  status: 'draft',
  questions: [emptyQuestion()],
})

const isQuestionnaireComplete = (values: QuestionnaireFormValues) => {
  if (!values.name.trim()) return false
  if (!values.status.trim()) return false
  if (!Array.isArray(values.questions) || values.questions.length === 0) {
    return false
  }

  return values.questions.every((question) => {
    if (!question.text.trim()) return false
    if (!question.type.trim()) return false

    if (!CHOICE_TYPES.includes(question.type)) {
      return true
    }

    return (
      Array.isArray(question.options) &&
      question.options.length > 0 &&
      question.options.every((option) => option.text.trim().length > 0)
    )
  })
}

const validationSchema = Yup.object({
  name: Yup.string()
    .trim()
    .max(255, 'El nombre debe tener máximo 255 caracteres')
    .required('El nombre del cuestionario es obligatorio'),
  description: Yup.string()
    .trim()
    .max(2000, 'La descripción debe tener máximo 2000 caracteres'),
  status: Yup.string()
    .oneOf(STATUS_OPTIONS.map((item) => item.value))
    .required('El estado es obligatorio'),
  questions: Yup.array()
    .of(
      Yup.object({
        text: Yup.string()
          .trim()
          .required('El enunciado de la pregunta es obligatorio'),
        type: Yup.string()
          .oneOf(QUESTION_TYPE_OPTIONS.map((item) => item.value))
          .required('El tipo de pregunta es obligatorio'),
        isRequired: Yup.boolean().required(),
        options: Yup.array()
          .of(
            Yup.object({
              text: Yup.string()
                .trim()
                .required('La respuesta es obligatoria'),
            })
          )
          .when('type', {
            is: (type: string) => CHOICE_TYPES.includes(type),
            then: (schema) =>
              schema.min(
                1,
                'Las preguntas de selección deben tener al menos una respuesta'
              ),
            otherwise: (schema) => schema.notRequired(),
          }),
      })
    )
    .min(1, 'Debes agregar al menos una pregunta'),
})

const QuestionaryForm = ({
  initialValues: customInitialValues,
  onSubmit,
  submitLabel = 'Guardar',
  successMessage = 'Cuestionario creado con éxito',
  errorMessage = 'No fue posible guardar el cuestionario',
}: IProps) => {
  const navigate = useNavigate()
  const { getError, getSuccess } = useNotification()
  const [loading, setLoading] = useState(false)

  const initialValues = useMemo<QuestionnaireFormValues>(
    () => customInitialValues || getDefaultInitialValues(),
    [customInitialValues]
  )

  const buildPayload = (
    values: QuestionnaireFormValues
  ): QuestionnairePayload => ({
    name: values.name.trim(),
    description: values.description.trim(),
    status: values.status,
    questions: values.questions.map((question, questionIndex) => ({
      id: question.id,
      text: question.text.trim(),
      type: question.type,
      is_required: question.isRequired,
      position: questionIndex + 1,
      options: CHOICE_TYPES.includes(question.type)
        ? question.options
            .filter((option) => option.text.trim().length > 0)
            .map((option, optionIndex) => ({
              id: option.id,
              text: option.text.trim(),
              position: optionIndex + 1,
            }))
        : [],
    })),
  })

  const submitQuestionnaire = async (values: QuestionnaireFormValues) => {
    if (loading) {
      return
    }

    const payload = buildPayload(values)

    try {
      setLoading(true)

      if (onSubmit) {
        await onSubmit(payload)
      } else {
        await axios.post('/company-questionnaire', payload)
      }

      getSuccess(successMessage)
      navigate(superAdminRoutes.Cuestionario)
    } catch (error: any) {
      const message =
        error?.response?.data?.message ||
        error?.message ||
        errorMessage
      getError(message)
    } finally {
      setLoading(false)
    }
  }

  return (
    <Container>
      <Formik
        enableReinitialize
        initialValues={initialValues}
        validateOnChange
        validateOnMount
        validationSchema={validationSchema}
        onSubmit={submitQuestionnaire}
      >
        {({
          values,
          errors,
          touched,
          handleChange,
          handleBlur,
          setFieldValue,
          isValid,
        }) => {
          const canSubmit = isValid && isQuestionnaireComplete(values) && !loading

          return (
            <Form style={{ width: '100%' }}>
            <Block display="flex" flexDirection="column">
              <Row width="100%" gap="20px">
                <Row width="48%" flexDirection="column">
                  <Input
                    name="name"
                    type="text"
                    label="Nombre del cuestionario"
                    onChange={handleChange}
                    onBlur={handleBlur}
                    value={values.name}
                  />
                  {errors.name && touched.name ? (
                    <ErrorContainer>
                      <TextError>{errors.name}</TextError>
                    </ErrorContainer>
                  ) : null}
                </Row>
                <Row width="48%" flexDirection="column">
                  <Select
                    hideNeverChoice={true}
                    label="Estado del cuestionario"
                    options={STATUS_OPTIONS}
                    onChange={(value) => setFieldValue('status', value)}
                    value={values.status}
                  />
                  {errors.status && touched.status ? (
                    <ErrorContainer>
                      <TextError>{errors.status}</TextError>
                    </ErrorContainer>
                  ) : null}
                </Row>
              </Row>

              <Row width="100%" margin="20px 0 0 0">
                <Row width="100%" flexDirection="column">
                  <Input
                    name="description"
                    type="textarea"
                    label="Descripción del cuestionario"
                    onChange={handleChange}
                    onBlur={handleBlur}
                    value={values.description}
                    style={{ height: '130px' }}
                    minRows={5}
                    maxRows={8}
                  />
                  {errors.description && touched.description ? (
                    <ErrorContainer>
                      <TextError>{errors.description}</TextError>
                    </ErrorContainer>
                  ) : null}
                </Row>
              </Row>

              <InfoContainer>
                <Row
                  justifyContent="space-between"
                  alignItems="center"
                  margin="0 0 10px 0"
                >
                  <H2>Preguntas del cuestionario</H2>
                  <StrokedButton
                    type="button"
                    onClick={() => {
                      setFieldValue('questions', [
                        ...values.questions,
                        emptyQuestion(),
                      ])
                    }}
                  >
                    Agregar pregunta
                  </StrokedButton>
                </Row>
                <P color="#797A7C" margin="0px 0px 10px 0px">
                  Cada cuestionario debe tener al menos una pregunta. Las
                  preguntas de selección deben incluir una o varias respuestas.
                </P>

                <FieldArray name="questions">
                  {({ remove, push }) => (
                    <>
                      {values.questions.map((question, questionIndex) => {
                        const questionErrors =
                          Array.isArray(errors.questions) &&
                          errors.questions[questionIndex]
                            ? errors.questions[questionIndex]
                            : undefined
                        const questionTouched =
                          Array.isArray(touched.questions) &&
                          touched.questions[questionIndex]
                            ? touched.questions[questionIndex]
                            : undefined

                        return (
                          <QuestionCard key={`question-${questionIndex}`}>
                            <ActionRow>
                              <H3 color="#1F2124" margin="0px">
                                Pregunta {questionIndex + 1}
                              </H3>
                              {values.questions.length > 1 && (
                                <TextButton
                                  onClick={() => {
                                    remove(questionIndex)
                                  }}
                                >
                                  Eliminar pregunta
                                </TextButton>
                              )}
                            </ActionRow>

                            <Row width="100%" gap="20px" margin="16px 0 0 0">
                              <Row width="60%" flexDirection="column">
                                <Input
                                  name={`questions.${questionIndex}.text`}
                                  type="textarea"
                                  label="Enunciado de la pregunta"
                                  onChange={handleChange}
                                  onBlur={handleBlur}
                                  value={question.text}
                                  style={{ height: '110px' }}
                                  minRows={4}
                                  maxRows={6}
                                />
                                {questionErrors &&
                                typeof questionErrors === 'object' &&
                                'text' in questionErrors &&
                                questionTouched &&
                                typeof questionTouched === 'object' &&
                                'text' in questionTouched ? (
                                  <ErrorContainer>
                                    <TextError>
                                      {questionErrors.text as string}
                                    </TextError>
                                  </ErrorContainer>
                                ) : null}
                              </Row>

                              <Row width="38%" flexDirection="column">
                                <Select
                                  hideNeverChoice={true}
                                  label="Tipo de pregunta"
                                  options={QUESTION_TYPE_OPTIONS}
                                  onChange={(value) => {
                                    setFieldValue(
                                      `questions.${questionIndex}.type`,
                                      value
                                    )
                                    if (CHOICE_TYPES.includes(value)) {
                                      if (question.options.length === 0) {
                                        setFieldValue(
                                          `questions.${questionIndex}.options`,
                                          [emptyOption()]
                                        )
                                      }
                                    } else {
                                      setFieldValue(
                                        `questions.${questionIndex}.options`,
                                        []
                                      )
                                    }
                                  }}
                                  value={question.type}
                                />
                                {questionErrors &&
                                typeof questionErrors === 'object' &&
                                'type' in questionErrors &&
                                questionTouched &&
                                typeof questionTouched === 'object' &&
                                'type' in questionTouched ? (
                                  <ErrorContainer>
                                    <TextError>
                                      {questionErrors.type as string}
                                    </TextError>
                                  </ErrorContainer>
                                ) : null}

                                <Row margin="18px 0 0 0" flexDirection="column">
                                  <Select
                                    hideNeverChoice={true}
                                    label="¿La pregunta es obligatoria?"
                                    options={[
                                      { value: 'true', label: 'Sí' },
                                      { value: 'false', label: 'No' },
                                    ]}
                                    onChange={(value) =>
                                      setFieldValue(
                                        `questions.${questionIndex}.isRequired`,
                                        value === 'true'
                                      )
                                    }
                                    value={
                                      question.isRequired ? 'true' : 'false'
                                    }
                                  />
                                </Row>
                              </Row>
                            </Row>

                            {CHOICE_TYPES.includes(question.type) && (
                              <FieldArray
                                name={`questions.${questionIndex}.options`}
                              >
                                {({
                                  remove: removeOption,
                                  push: pushOption,
                                }) => (
                                  <Row
                                    flexDirection="column"
                                    margin="24px 0 0 0"
                                  >
                                    <ActionRow>
                                      <H3 color="#1F2124" margin="0px">
                                        Respuestas
                                      </H3>
                                      <StrokedButton
                                        type="button"
                                        onClick={() => {
                                          pushOption(emptyOption())
                                        }}
                                      >
                                        Agregar respuesta
                                      </StrokedButton>
                                    </ActionRow>

                                    {question.options.map(
                                      (option, optionIndex) => {
                                        const optionErrors =
                                          questionErrors &&
                                          typeof questionErrors === 'object' &&
                                          'options' in questionErrors &&
                                          Array.isArray(questionErrors.options)
                                            ? questionErrors.options[
                                                optionIndex
                                              ]
                                            : undefined
                                        const optionTouched =
                                          questionTouched &&
                                          typeof questionTouched === 'object' &&
                                          'options' in questionTouched &&
                                          Array.isArray(questionTouched.options)
                                            ? questionTouched.options[
                                                optionIndex
                                              ]
                                            : undefined

                                        return (
                                          <OptionCard
                                            key={`question-${questionIndex}-option-${optionIndex}`}
                                          >
                                            <ActionRow>
                                              <P
                                                color="#1F2124"
                                                weight={700}
                                                margin="0px"
                                              >
                                                Respuesta {optionIndex + 1}
                                              </P>
                                              {question.options.length > 1 && (
                                                <TextButton
                                                  onClick={() => {
                                                    removeOption(optionIndex)
                                                  }}
                                                >
                                                  Eliminar respuesta
                                                </TextButton>
                                              )}
                                            </ActionRow>

                                            <Row
                                              width="100%"
                                              margin="12px 0 0 0"
                                            >
                                              <Row
                                                width="100%"
                                                flexDirection="column"
                                              >
                                                <Input
                                                  name={`questions.${questionIndex}.options.${optionIndex}.text`}
                                                  type="text"
                                                  label="Texto de la respuesta"
                                                  onChange={handleChange}
                                                  onBlur={handleBlur}
                                                  value={option.text}
                                                />
                                                {optionErrors &&
                                                typeof optionErrors ===
                                                  'object' &&
                                                'text' in optionErrors &&
                                                optionTouched &&
                                                typeof optionTouched ===
                                                  'object' &&
                                                'text' in optionTouched ? (
                                                  <ErrorContainer>
                                                    <TextError>
                                                      {optionErrors.text as string}
                                                    </TextError>
                                                  </ErrorContainer>
                                                ) : null}
                                              </Row>
                                            </Row>
                                          </OptionCard>
                                        )
                                      }
                                    )}

                                    {questionErrors &&
                                    typeof questionErrors === 'object' &&
                                    'options' in questionErrors &&
                                    typeof questionErrors.options === 'string' ? (
                                      <ErrorContainer>
                                        <TextError>
                                          {questionErrors.options}
                                        </TextError>
                                      </ErrorContainer>
                                    ) : null}
                                  </Row>
                                )}
                              </FieldArray>
                            )}
                          </QuestionCard>
                        )
                      })}

                      <Row margin="24px 0 0 0">
                        <StrokedButton
                          type="button"
                          onClick={() => {
                            push(emptyQuestion())
                          }}
                        >
                          Agregar otra pregunta
                        </StrokedButton>
                      </Row>
                    </>
                  )}
                </FieldArray>

                {typeof errors.questions === 'string' ? (
                  <ErrorContainer>
                    <TextError>{errors.questions}</TextError>
                  </ErrorContainer>
                ) : null}
              </InfoContainer>
            </Block>

            <LastButtonsContainer>
              <P
                onClick={() => {
                  if (loading) return
                  navigate(-1)
                }}
                style={{ cursor: 'pointer' }}
                weight={700}
                color="#2D749C"
              >
                Cancelar
              </P>
              <Button
                disabled={!canSubmit}
                type="submit"
              >
                {loading ? 'Guardando...' : submitLabel}
              </Button>
            </LastButtonsContainer>
          </Form>
          )
        }}
      </Formik>
    </Container>
  )
}

export default QuestionaryForm
export type { QuestionnaireFormValues, QuestionnairePayload }
